import { inject, Injectable } from '@angular/core';
import { FormGroup, ValidationErrors } from '@angular/forms';
import { ApiException, isUnityValidationProblem, UnityValidationError } from '@unity/clients';
import { ToasterService } from '@unity/services/toaster.service';
import { catchError, EMPTY, map, Observable, take, tap } from 'rxjs';
import { NavigationService } from './navigation.service';

@Injectable({
  providedIn: 'root',
})
export class DetailFormService {
  private readonly navigationService = inject(NavigationService);
  private readonly toasterService = inject(ToasterService);

  public handleServerValidationErrors(formGroup: FormGroup, errors: Record<string, UnityValidationError[]>) {
    if (errors['']) {
      this.toasterService.showError('Form invalid', new Error(errors[''].join('. ')), 'warning');
    }

    formGroup.enable();
    for (const key of Object.keys(errors)) {
      const control = formGroup.get(key);
      const errorMessage = errors[key].map(x => x.message).join('. ');
      if (control) {
        control.setErrors({
          serverError: errorMessage,
        });
        this.toasterService.showError('Form invalid', new Error(errorMessage), 'warning');
      }
    }
  }

  private handleUpdateError = (formGroup: FormGroup) =>
    catchError((e: Error) => {
      if (ApiException.isApiException(e) && isUnityValidationProblem(e.result)) {
        this.handleServerValidationErrors(formGroup, e.result.errors ?? {});
      } else {
        this.toasterService.showError('Error while saving changes', e, 'error');
        formGroup.reset();
        formGroup.updateValueAndValidity();
      }
      return EMPTY;
    });

  private handleUpdateSuccess = (formGroup: FormGroup) =>
    tap(() => {
      this.toasterService.showInfo('Changes saved.');
      formGroup.markAsPristine();
    });

  private handleDeleteError = () =>
    catchError((e: Error) => {
      this.toasterService.showError('Error while deleting', e, 'error');
      return EMPTY;
    });

  private handleDeleteSuccess = (resourceName: string) =>
    tap(() => {
      this.toasterService.showInfo(`${resourceName} has been deleted`);
      return this.navigationService.navigateUp();
    });

  public updateResource$(formGroup: FormGroup, update$: Observable<unknown>) {
    return update$.pipe(
      this.handleUpdateError(formGroup),
      this.handleUpdateSuccess(formGroup),
      map(() => null),
      take(1)
    );
  }

  public deleteResource$(delete$: Observable<unknown>, resourceName: string) {
    return delete$.pipe(
      this.handleDeleteError(),
      this.handleDeleteSuccess(resourceName),
      map(() => null),
      take(1)
    );
  }

  public handleFormErrors(formGroup: FormGroup) {
    const errors: Record<string, ValidationErrors> = {};
    if (formGroup.errors) {
      errors['Form'] = formGroup.errors ?? [];
    }
    for (const [key, control] of Object.entries(formGroup.controls)) {
      if (control.errors) {
        errors[key] = control.errors;
      }
    }
    const messages = JSON.stringify(errors);
    this.toasterService.showError('Form invalid', new Error(messages), 'warning');

    formGroup.markAllAsTouched();
  }
}
